<script lang="ts">
  import { Cuentas, Seguros, type Cuenta, type Renglon, type Aseguradora, type CoberturaResult } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';

  let items = $state<Cuenta[]>([]);
  let aseguradoras = $state<Aseguradora[]>([]);
  let cobertura = $state<Record<string, CoberturaResult>>({});
  let loading = $state(false);
  let error = $state<string | null>(null);
  let expandida = $state<string | null>(null);

  let nueva = $state<Cuenta>({ pacienteId: '', aseguradora: '', aseguradoraId: '', deducible: 0, copago: 0 });
  let nuevoRenglon = $state<Record<string, Renglon>>({});

  async function cargar() {
    loading = true; error = null;
    try {
      items = await Cuentas.list();
      aseguradoras = await Seguros.list(true).catch(() => []);
    }
    catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  function elegirAseguradora(id: string) {
    const a = aseguradoras.find(x => x.id === id);
    nueva.aseguradoraId = id || '';
    nueva.aseguradora = a?.nombre ?? '';
  }

  async function abrir(e: SubmitEvent) {
    e.preventDefault();
    if (!nueva.pacienteId) { error = 'Selecciona un paciente.'; return; }
    try {
      const body: Cuenta = {
        ...nueva,
        aseguradoraId: nueva.aseguradoraId || null,
        deducible: Number(nueva.deducible ?? 0),
        copago: Number(nueva.copago ?? 0),
      };
      const abierta = await Cuentas.abrir(body);
      if (abierta.id && body.aseguradoraId) {
        await Seguros.calcular(abierta.id, {
          aseguradoraId: body.aseguradoraId,
          deducible: Number(body.deducible ?? 0),
          copago: Number(body.copago ?? 0),
        }).catch(() => {});
      }
      nueva = { pacienteId: '', aseguradora: '', aseguradoraId: '', deducible: 0, copago: 0 };
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  function rowFor(id: string): Renglon {
    if (!nuevoRenglon[id]) nuevoRenglon[id] = { descripcion: '', cantidad: 1, precioUnitario: 0 };
    return nuevoRenglon[id];
  }

  async function agregarRenglon(c: Cuenta) {
    const r = rowFor(c.id!);
    if (!r.descripcion.trim()) return;
    try {
      await Cuentas.renglon(c.id!, {
        descripcion: r.descripcion,
        cantidad: Number(r.cantidad),
        precioUnitario: Number(r.precioUnitario)
      });
      nuevoRenglon[c.id!] = { descripcion: '', cantidad: 1, precioUnitario: 0 };
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cerrar(c: Cuenta) {
    if (!confirm('¿Cerrar cuenta?')) return;
    try {
      if (c.id && (c.aseguradoraId || c.aseguradora)) {
        const r = await Seguros.calcular(c.id, {
          aseguradoraId: c.aseguradoraId || undefined,
          deducible: Number(c.deducible ?? 0),
          copago: Number(c.copago ?? 0),
        });
        cobertura = { ...cobertura, [c.id]: r };
      }
      await Cuentas.cerrar(c.id!);
      await cargar();
    }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });
</script>

<section>
  <header>
    <h2>Cuentas de pacientes</h2>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}

  <form onsubmit={abrir} class="row">
    <PacientePicker bind:value={nueva.pacienteId} onPick={(id) => nueva.pacienteId = id} />
    <select value={nueva.aseguradoraId ?? ''} onchange={(e) => elegirAseguradora((e.currentTarget as HTMLSelectElement).value)}>
      <option value="">— Particular (sin seguro) —</option>
      {#each aseguradoras as a (a.id)}
        <option value={a.id}>{a.nombre} · {a.porcentajeDefault}%</option>
      {/each}
    </select>
    {#if nueva.aseguradoraId}
      <input type="number" min="0" step="0.01" placeholder="Deducible" bind:value={nueva.deducible} class="small" />
      <input type="number" min="0" step="0.01" placeholder="Copago" bind:value={nueva.copago} class="small" />
    {/if}
    <button type="submit" disabled={!nueva.pacienteId}>Abrir cuenta</button>
  </form>

  {#each items as c (c.id)}
    <article class="cuenta">
      <div class="head">
        <div>
          <strong>Paciente</strong> <code>{c.pacienteId.slice(0, 8)}…</code>
          <span class="badge {c.estado?.toLowerCase()}">{c.estado}</span>
          {#if c.aseguradora}<span class="aseg">{c.aseguradora}</span>{/if}
          {#if Number(c.montoCubierto ?? 0) > 0 || cobertura[c.id!]}
            {@const cov = cobertura[c.id!]}
            <span class="split">Paciente Q {Number(cov?.pagaPaciente ?? (Number(c.total ?? 0) - Number(c.montoCubierto ?? 0))).toFixed(2)}
              · Seguro Q {Number(cov?.pagaAseguradora ?? c.montoCubierto ?? 0).toFixed(2)}</span>
          {/if}
        </div>
        <div>
          <strong class="total">Q {Number(c.total ?? 0).toFixed(2)}</strong>
          <button onclick={() => expandida = expandida === c.id ? null : c.id!}>
            {expandida === c.id ? 'Cerrar' : 'Ver detalle'}
          </button>
          {#if c.estado === 'ABIERTA'}
            <button class="danger" onclick={() => cerrar(c)}>Cerrar cuenta</button>
          {/if}
        </div>
      </div>

      {#if expandida === c.id}
        <table class="renglones">
          <thead><tr><th>Descripción</th><th>Cant.</th><th>P.U.</th><th>Total</th></tr></thead>
          <tbody>
            {#each (c.renglones ?? []) as r (r.id)}
              <tr>
                <td>{r.descripcion}</td>
                <td class="num">{r.cantidad}</td>
                <td class="num">Q {Number(r.precioUnitario).toFixed(2)}</td>
                <td class="num">Q {Number(r.total ?? 0).toFixed(2)}</td>
              </tr>
            {:else}
              <tr><td colspan="4" class="empty">No hay renglones en esta cuenta.</td></tr>
            {/each}
          </tbody>
        </table>

        {#if c.estado === 'ABIERTA'}
          {@const r = rowFor(c.id!)}
          <div class="add">
            <input placeholder="Descripción" bind:value={r.descripcion} />
            <input type="number" min="1" placeholder="Cantidad" bind:value={r.cantidad} class="small" />
            <input type="number" min="0" step="0.01" placeholder="Precio unitario" bind:value={r.precioUnitario} class="small" />
            <button onclick={() => agregarRenglon(c)}>+ Renglón</button>
          </div>
        {/if}
      {/if}
    </article>
  {:else}
    <p class="empty">No hay cuentas abiertas.</p>
  {/each}
</section>

<style>
  header { display: flex; justify-content: space-between; align-items: center; }
  .row { display: flex; gap: .5rem; flex-wrap: wrap; margin: 1rem 0; }
  input, select, button { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #ccc; font: inherit; }
  button { background: #0d6efd; color: #fff; border: 0; cursor: pointer; }
  button.danger { background: #b00020; }
  .cuenta { border: 1px solid #e5e5e5; border-radius: 10px; padding: .8rem 1rem; margin-bottom: .8rem; background: #fff; }
  .head { display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; }
  .badge { padding: .15rem .55rem; border-radius: 999px; font-size: .75rem; margin-left: .5rem; }
  .badge.abierta { background: #d6f5d6; color: #1a6b1a; }
  .badge.cerrada { background: #e0e0e0; color: #555; }
  .aseg { background: #eef; padding: .15rem .5rem; border-radius: 6px; margin-left: .5rem; font-size: .8rem; }
  .split { margin-left: .5rem; font-size: .8rem; color: #166534; font-weight: 600; }
  .row .small { width: 110px; }
  .total { font-size: 1.15rem; color: #0d6efd; margin-right: .8rem; }
  .renglones { width: 100%; border-collapse: collapse; margin-top: .8rem; }
  .renglones th, .renglones td { padding: .4rem .55rem; border-bottom: 1px solid #f0f0f0; font-size: .9rem; text-align: left; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .add { display: flex; gap: .4rem; margin-top: .6rem; flex-wrap: wrap; }
  .add input { flex: 1; min-width: 140px; }
  .add .small { flex: 0 0 90px; }
  code { font-size: .8rem; background: #f4f4f4; padding: .1rem .35rem; border-radius: 4px; }
  .err { color: #b00020; }
  .empty { color: #888; font-style: italic; text-align: center; }
</style>
